import './PeriscopeMenuTabs.scss';

import { ArrowRightOutlined } from '@ant-design/icons';
import { FeatureFlag } from '@sonar-software/sonar-core';
import { PeriscopeMenuLabel } from './PeriscopeMenuLabel';
import { sonarHttpGet } from '@sonar-software/sonar-http';

import { getOrAddEntry, isUndefined } from '@sonar-software/sonar-ts-lib';
import { IsEnabledFeatureFlag, useFeatureFlags, useOnMount } from '@hooks';
import { Menu, MenuProps, TabPaneProps, Tabs } from 'antd';
import { MenuInfo, MenuItemType, SubMenuType } from 'rc-menu/es/interface';
import React, { PropsWithChildren, useMemo, useRef, useState } from 'react';

interface OptionalCount {
	count?: number | Promise<number>;
}

type ItemsType = Required<MenuProps>['items'];
type ItemType = ItemsType[number];

type SubmenuItemWithCountType = SubMenuType & OptionalCount;
type MenuItemWithCountType = MenuItemType & OptionalCount;
type TabItemType = Omit<TabPaneProps, 'tab'> & { key: string; label: string };
type TabCountLookup = Record<string, number | Promise<number> | undefined>;

function initializeTabCounts(
	children: Array<React.ReactNode>,
	setTabCounts: React.Dispatch<React.SetStateAction<TabCountLookup>>
): TabCountLookup {
	const result: TabCountLookup = {};

	for (let i = 0; i < children.length; i += 1) {
		const { key, props } = children[ i ] as React.ReactElement<PeriscopeTabAreaProps>;
		const { count, countEndpoint } = props;
		const stringKey = String(key);

		if (!isUndefined(count) || isUndefined(countEndpoint)) {
			result[ stringKey ] = count;
		} else if (countEndpoint) {
			result[ stringKey ] = sonarHttpGet<void, number>({
				targetUri: countEndpoint
			}).then((response) => {
				const value = response.data ?? 0;

				setTabCounts((counts) => ({
					...counts,
					[ stringKey ]: value
				}));

				return value;
			});
		}
	}

	return result;
}

function updateCountLabels(menuItem: ItemType): void {
	const submenu = menuItem as SubmenuItemWithCountType;

	if (!submenu.children?.length) {
		return;
	}

	for (const child of submenu.children) {
		updateCountLabels(child as ItemType);
	}

	const countChildren = submenu.children.filter((child: MenuItemWithCountType) => !isUndefined(child.count));

	if (!countChildren.length) {
		return;
	}

	const sumPromise = Promise.all(
		countChildren.map((child: MenuItemWithCountType) => Promise.resolve(child.count ?? 0))
	).then((values) => values.reduce((prev, curr) => (prev ?? 0) + (curr ?? 0), 0));

	submenu.count = sumPromise;
	submenu.label = <PeriscopeMenuLabel
		count={ sumPromise }
		name={ submenu.label } />;
}

function getEnabledChildren(children: React.ReactNode, isEnabled: IsEnabledFeatureFlag): Array<React.ReactNode> {
	return React.Children.toArray(children).filter((child: React.ReactElement<PeriscopeTabAreaProps>) => {
		if (child.props.hidden) {
			return false;
		}

		if (!child.props.featureFlag) {
			return true;
		}

		return isEnabled(child.props.featureFlag);
	});
}

function buildMenuItems(
	children: Array<React.ReactNode>,
	setOpenKeys: React.Dispatch<React.SetStateAction<Array<string>>>,
	activeKeyPathRef: React.MutableRefObject<Array<string>>,
	tabCounts: TabCountLookup
): ItemsType {
	const results = [];
	const submenuMap: Record<string, SubmenuItemWithCountType> = {};
	const groupMap: Record<string, SubmenuItemWithCountType> = {};

	const onSubmenuClick = (info: MenuInfo) => {
		if (info.key === activeKeyPathRef.current[ 0 ] && activeKeyPathRef.current.length > 1) {
			setTimeout(() => {
				setOpenKeys(activeKeyPathRef.current);
			}, 150);
		}
	};

	const onSubmenuGroupMouseEnter = (info: { key: string }) => {
		setOpenKeys((keys) => {
			if (keys.indexOf(info.key) === -1) {
				return [ ...keys, info.key ];
			}

			return keys;
		});
	};

	const onSubmenuGroupMouseLeave = (info: { key: string }) => {
		setOpenKeys((keys) => {
			const index = keys.indexOf(info.key);
			if (index !== -1) {
				return [ ...keys.slice(0, index), ...keys.slice(index + 1) ];
			}

			return keys;
		});
	};

	for (let i = 0; i < children.length; i += 1) {
		const { key, props } = children[ i ] as React.ReactElement<PeriscopeTabAreaProps>;
		const { disabled, group, requiresCount, submenu, tab } = props;

		let targetSubmenu: SubmenuItemWithCountType | undefined = undefined;
		let targetGroup: SubmenuItemWithCountType | undefined = undefined;

		if (submenu) {
			targetSubmenu = getOrAddEntry(submenu, submenuMap, () => {
				const newSubmenu: SubmenuItemWithCountType = {
					children: [],
					key: submenu,
					label: submenu,
					onTitleClick: onSubmenuClick,
					popupClassName: 'periscope-tab-submenu',
					popupOffset: [ -10, 0 ]
				};

				results.push(newSubmenu);

				return newSubmenu;
			});
		}

		if (group) {
			targetGroup = getOrAddEntry(group, groupMap, () => {
				const newGroup: SubmenuItemWithCountType = {
					children: [],
					key: group,
					label: group,
					onMouseEnter: onSubmenuGroupMouseEnter,
					onMouseLeave: onSubmenuGroupMouseLeave,
					popupOffset: [ 0, -4 ]
				};

				if (targetSubmenu) {
					targetSubmenu.children.push(newGroup);
				} else {
					results.push(newGroup);
				}

				return newGroup;
			});
		}

		if (targetSubmenu && targetGroup && !targetSubmenu.children.includes(targetGroup)) {
			targetSubmenu.children.push(targetGroup);
		}

		const count = tabCounts[ String(key) ];
		const isDisabled = disabled || (requiresCount && (!count || isNaN(Number(count))));

		const entry: MenuItemWithCountType = {
			count,
			disabled: isDisabled,
			key: key as React.Key,
			label: <PeriscopeMenuLabel
				count={ count }
				name={ tab } />
		};

		if (targetGroup) {
			targetGroup.children?.push(entry);
		} else if (!targetGroup && targetSubmenu) {
			targetSubmenu.children.push(entry);
		} else {
			results.push(entry);
		}
	}

	for (const entry of results) {
		updateCountLabels(entry as ItemType);
	}

	return results as ItemsType;
}

function buildTabItems(children: Array<React.ReactNode>): Array<TabItemType> {
	return children.map(({ key, props }: React.ReactElement<PeriscopeTabAreaProps>) => ({
		children: props.children,
		forceRender: props.forceRender,
		key: String(key),
		label: String(key)
	}));
}

export interface PeriscopeMenuTabsProps {
	defaultKey?: string;
}

export const PeriscopeMenuTabs: React.FC<PropsWithChildren<PeriscopeMenuTabsProps>> = ({ children, defaultKey }) => {
	const { isEnabled } = useFeatureFlags();
	const [ tabItems, setTabItems ] = useState<Array<TabItemType>>([]);
	const [ activeKey, setActiveKey ] = useState(defaultKey ? `.$${defaultKey}` : undefined);
	const [ openKeys, setOpenKeys ] = useState<Array<string>>([]);
	const [ tabCounts, setTabCounts ] = useState<TabCountLookup>();

	const availableChildrenRef = useRef<Array<React.ReactNode>>(getEnabledChildren(children, isEnabled));

	const activeKeyPathRef = useRef<Array<string>>([]);

	useOnMount(() => {
		setTabCounts(initializeTabCounts(availableChildrenRef.current, setTabCounts));
		setTabItems(buildTabItems(availableChildrenRef.current));
	});

	const menuItems = useMemo(
		() => (tabCounts ? buildMenuItems(availableChildrenRef.current, setOpenKeys, activeKeyPathRef, tabCounts) : []),
		[ tabCounts ]
	);

	return (
		<Tabs
			activeKey={ activeKey }
			className='periscope-tab'
			items={ tabItems }
			onChange={ setActiveKey }
			renderTabBar={ (props) =>
				<div className='ant-tabs-nav'>
					<Menu
						disabledOverflow
						expandIcon={ <ArrowRightOutlined /> }
						items={ menuItems }
						mode='horizontal'
						onClick={ (clickEvent) => {
							props.onTabClick(clickEvent.key, clickEvent.domEvent);
							activeKeyPathRef.current = clickEvent.keyPath.slice(1).reverse();
						} }
						onOpenChange={ (keys) => setOpenKeys(keys) }
						openKeys={ openKeys }
						selectedKeys={ [ props.activeKey ] }
						triggerSubMenuAction='click' />
				</div> } />
	);
};

export interface PeriscopeTabAreaProps {
	children: React.ReactNode;
	count?: number;
	countEndpoint?: string;
	disabled?: boolean;
	forceRender?: boolean;
	featureFlag?: FeatureFlag | FeatureFlag[];
	group?: string;
	hidden?: boolean;
	key: React.Key;
	requiresCount?: boolean;
	submenu?: string;
	tab: string | React.ReactNode;
}

export const PeriscopeTabArea: React.FC<PeriscopeTabAreaProps> = () => null;
